/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import { defineMessages } from 'react-intl';

export const common = defineMessages({
  logoAltText: {
    id: 'logoAltText',
    defaultMessage: '{institutionName} logo',
  },
  usernameLabel: {
    id: 'usernameLabel',
    defaultMessage: 'Username',
  },
  passwordLabel: {
    id: 'passwordLabel',
    defaultMessage: 'Password',
  },
  password: {
    id: 'password',
    defaultMessage: 'password',
  },
  usernameIsRequired: {
    id: 'usernameIsRequired',
    defaultMessage: 'Username is required.',
  },
  passwordIsRequired: {
    id: 'passwordIsRequired',
    defaultMessage: 'Password is required.',
  },
  confirmField: {
    id: 'confirmField',
    defaultMessage: 'Confirm {fieldName}',
  },
  fieldIsRequired: {
    id: 'fieldIsRequired',
    defaultMessage: '{fieldName} is required.',
  },
  currentFieldIsRequired: {
    id: 'currentFieldIsRequired',
    defaultMessage: 'Current {fieldName} is required.',
  },
  currentPasswordIsRequired: {
    id: 'currentPasswordIsRequired',
    defaultMessage: 'Current password is required.',
  },
  newPasswordIsRequired: {
    id: 'newPasswordIsRequired',
    defaultMessage: 'New password is required.',
  },
  newFieldIsRequired: {
    id: 'newFieldIsRequired',
    defaultMessage: 'New {fieldName} is required.',
  },
  confirmPasswordIsRequired: {
    id: 'confirmPasswordIsRequired',
    defaultMessage: 'Confirm password is required.',
  },
  confirmNewPasswordIsRequired: {
    id: 'confirmNewPasswordIsRequired',
    defaultMessage: 'Confirm new password is required.',
  },
  confirmFieldIsRequired: {
    id: 'confirmFieldIsRequired',
    defaultMessage: 'Confirm {fieldName} is required.',
  },
  confirmNewFieldIsRequired: {
    id: 'confirmNewFieldIsRequired',
    defaultMessage: 'Confirm new {fieldName} is required.',
  },
  showFieldEntry: {
    id: 'showFieldEntry',
    defaultMessage: 'Show {fieldName} entry',
  },
  hideFieldEntry: {
    id: 'hideFieldEntry',
    defaultMessage: 'Hide {fieldName} entry',
  },
  signIn: {
    id: 'signIn',
    defaultMessage: 'Sign in',
  },
  cancel: {
    id: 'cancel',
    defaultMessage: 'Cancel',
  },
  expand: {
    id: 'expand',
    defaultMessage: 'Expand',
  },
  collapse: {
    id: 'collapse',
    defaultMessage: 'Collapse',
  },
  expandText: {
    id: 'expandText',
    defaultMessage: 'Expand text.',
  },
  collapseText: {
    id: 'collapseText',
    defaultMessage: 'Collapse text.',
  },
  requestField: {
    id: 'requestField',
    defaultMessage: 'Request {fieldName}',
  },
  requestPassword: {
    id: 'requestPassword',
    defaultMessage: 'Request password',
  },
  fieldRequirementLabel: {
    id: 'fieldRequirementLabel',
    defaultMessage: '{fieldName} requirements',
  },
  defaultFieldRequirementLabel: {
    id: 'defaultFieldRequirementLabel',
    defaultMessage: 'Password requirements',
  },
  usernameRequirements: {
    id: 'usernameRequirements',
    defaultMessage: 'Username requirements',
  },
  allFieldsRequired: {
    id: 'allFieldsRequired',
    defaultMessage: 'All fields required.',
  },
  changePassword: {
    id: 'changePassword',
    defaultMessage: 'Change password',
  },
  changeField: {
    id: 'changeField',
    defaultMessage: 'Change {fieldName}',
  },
  currentPassword: {
    id: 'currentPassword',
    defaultMessage: 'Current password',
  },
  currentFieldLabel: {
    id: 'currentFieldLabel',
    defaultMessage: 'Current {fieldName}',
  },
  newPassword: {
    id: 'newPassword',
    defaultMessage: 'New password',
  },
  newFieldLabel: {
    id: 'newFieldLabel',
    defaultMessage: 'New {fieldName}',
  },
  confirmPassword: {
    id: 'confirmPassword',
    defaultMessage: 'Confirm password',
  },
  confirmNewPassword: {
    id: 'confirmNewPassword',
    defaultMessage: 'Confirm new password',
  },
  confirmNewFieldLabel: {
    id: 'confirmNewFieldLabel',
    defaultMessage: 'Confirm new {fieldName}',
  },
  passwordRequirementsNotMet: {
    id: 'passwordRequirementsNotMet',
    defaultMessage: 'Password requirements not met.',
  },
  usernameRequirementsNotMet: {
    id: 'usernameRequirementsNotMet',
    defaultMessage: 'Username requirements not met.',
  },
  requirementsNotMet: {
    id: 'requirementsNotMet',
    defaultMessage: '{fieldName} requirements not met.',
  },
  passwordsDoNotMatch: {
    id: 'passwordsDoNotMatch',
    defaultMessage: 'Passwords do not match.',
  },
  newPasswordsDoNotMatch: {
    id: 'newPasswordsDoNotMatch',
    defaultMessage: 'New passwords do not match.',
  },
  fieldsDoNotMatch: {
    id: 'fieldsDoNotMatch',
    defaultMessage: '{fieldName}s do not match.',
  },
  newFieldsDoNotMatch: {
    id: 'newFieldsDoNotMatch',
    defaultMessage: 'New {fieldName}s do not match.',
  },
  invalidNewPassword: {
    id: 'invalidNewPassword',
    defaultMessage: 'New password is incorrect.',
  },
  invalidNewField: {
    id: 'invalidNewField',
    defaultMessage: 'New {fieldName} is incorrect.',
  },
  invalidRepeatedPassword: {
    id: 'invalidRepeatedPassword',
    defaultMessage: 'Confirm new password is incorrect.',
  },
  invalidRepeatedField: {
    id: 'invalidRepeatedField',
    defaultMessage: 'Confirm new {fieldName} is incorrect.',
  },
  passwordUpdated: {
    id: 'passwordUpdated',
    defaultMessage: 'Password updated',
  },
  fieldUpdated: {
    id: 'fieldUpdated',
    defaultMessage: '{fieldName} updated',
  },
  passwordUpdatedSuccessfully: {
    id: 'passwordUpdatedSuccessfully',
    defaultMessage: 'Password has been updated successfully.',
  },
  fieldUpdatedSuccessfully: {
    id: 'fieldUpdatedSuccessfully',
    defaultMessage: '{fieldName} has been updated successfully.',
  },
  createAccount: {
    id: 'createAccount',
    defaultMessage: 'Create account',
  },
  passwordChangeUnavailable: {
    id: 'passwordChangeUnavailable',
    defaultMessage: 'Password change unavailable',
  },
  fieldChangeUnavailable: {
    id: 'fieldChangeUnavailable',
    defaultMessage: '{fieldName} change unavailable',
  },
  authenticationUnavailable: {
    id: 'authenticationUnavailable',
    defaultMessage: 'Authentication unavailable',
  },
  authenticationRequestNotMet: {
    id: 'authenticationNotMet',
    defaultMessage:
      'Your authentication request cannot be met. Contact your library or OCLC support at support@oclc.org for assistance.',
  },
  returnToApplication: {
    id: 'returnToApplication',
    defaultMessage: 'Return to application',
  },
  defaultAuthenticationCanceledHeader: {
    id: 'defaultAuthenticationCanceledHeader',
    defaultMessage: 'Password change not authenticated',
  },
  authenticationCanceledHeader: {
    id: 'authenticationCanceledHeader',
    defaultMessage: '{fieldName} change not authenticated',
  },
  defaultAuthenticationCanceledMessage: {
    id: 'defaultAuthenticationCanceledMessage',
    defaultMessage: 'You must be authenticated to change your password.',
  },
  authenticationCanceledMessage: {
    id: 'authenticationCanceledMessage',
    defaultMessage: 'You must be authenticated to change your {fieldName}.',
  },
  success: {
    id: 'success',
    defaultMessage: 'Success',
  },
  error: {
    id: 'error',
    defaultMessage: 'Error',
  },
});

const authResult = defineMessages({
  AUTH_SUCCESS: {
    id: 'AUTH_SUCCESS',
    defaultMessage: 'Authentication successful',
  },
  TOO_MANY_FAILURE: {
    id: 'TOO_MANY_FAILURE',
    defaultMessage:
      'You have exceeded the allotted number of log-in attempts. Close your browser and wait 5 minutes before attempting to log-in again.',
  },
  AUTH_FAILURE: {
    id: 'AUTH_FAILURE',
    defaultMessage: 'Invalid credentials, try again.',
  },
  EXTERNAL_CONNECTION_FAILURE: {
    id: 'EXTERNAL_CONNECTION_FAILURE',
    defaultMessage:
      'We cannot reach your institution to verify your credentials. Try again later.',
  },
  CONFIGURATION_ERROR: {
    id: 'CONFIGURATION_ERROR',
    defaultMessage:
      'A configuration error has occurred. Try again later or contact your library for assistance.',
  },
  MULTIPLE_USERS_ERROR: {
    id: 'MULTIPLE_USERS_ERROR',
    defaultMessage:
      'Multiple users were found. Please contact your library support services team.',
  },
  AUTHORIZATION_FAILURE: {
    id: 'AUTHORIZATION_FAILURE',
    defaultMessage: 'You are not authorized to use this service.',
  },
  USER_NOT_FOUND: {
    id: 'USER_NOT_FOUND',
    defaultMessage: 'User not found',
  },
  USER_ACCOUNT_EXPIRED: {
    id: 'USER_ACCOUNT_EXPIRED',
    defaultMessage:
      'Your account has expired. Contact your library for assistance.',
  },
  UNKNOWN_ERROR: {
    id: 'UNKNOWN_ERROR',
    defaultMessage:
      'A system error has occurred. Please contact your institution representative.',
  },
  CERTIFICATE_ERROR: {
    id: 'CERTIFICATE_ERROR',
    defaultMessage:
      "OCLC has detected that your institution's encryption certificate has changed. Contact your library for assistance.",
  },
  BIND_USER_ERROR: {
    id: 'BIND_USER_ERROR',
    defaultMessage:
      "OCLC has detected a problem with your institution's authorization credentials. Contact your library for assistance.",
  },
  UDS_NOT_FOUND: {
    id: 'UDS_NOT_FOUND',
    defaultMessage: 'User record not found',
  },
});

export const errorMessages = defineMessages({
  usernameAlreadyInUse: {
    id: 'usernameAlreadyInUse',
    defaultMessage:
      'Username cannot be accepted, try again with a different username.',
  },
  fieldNameAlreadyInUse: {
    id: 'fieldNameAlreadyInUse',
    defaultMessage:
      '{fieldName} cannot be accepted, try again with a different {fieldName}.',
  },
  'page.recoverpassword.access-denied': {
    id: 'RecoverPassword.accessDenied',
    defaultMessage: 'Access denied',
  },
  'samllogin.page.error.cant.get.institutionid.from.hostname': {
    id: 'samllogin.page.error.cant.get.institutionid.from.hostname',
    defaultMessage:
      'We are unable to process your request at this moment. Please try again later.',
  },
  'access.denied': {
    id: 'accessDenied',
    defaultMessage: 'Access denied',
  },
  'system.error': {
    id: 'system.error',
    defaultMessage: 'Unable to complete action. An error has occurred.',
  },
  'page.resetpassword.invalid-new-password': {
    id: 'ResetPassword.invalidNewPassword',
    defaultMessage: 'New password is invalid',
  },
  'page.resetpassword.new-password-mismatch': {
    id: 'ResetPassword.newPasswordMismatch',
    defaultMessage: 'New passwords do not match.',
  },
  'page.resetpassword.invalid-recovery-key': {
    id: 'ResetPassword.invalidRecoveryKey',
    defaultMessage:
      'Your recovery key is invalid. Re-submit {fieldName} recovery <link>request</link>.',
  },
  'page.recoverpassword.email-failure': {
    id: 'RecoverPassword.emailFailure',
    defaultMessage:
      'The system is unable to send you an email. Contact your library for assistance.',
  },
  notAuthorized: {
    id: 'notAuthorized',
    defaultMessage: 'Not authorized',
  },
  notAuthorizedToChangePassword: {
    id: 'notAuthorizedToChangePassword',
    defaultMessage:
      'You are not authorized to change the password. Contact your library for assistance.',
  },
  notAuthorizedToChangeField: {
    id: 'notAuthorizedToChangeField',
    defaultMessage:
      'You are not authorized to change the {fieldName}. Contact your library for assistance.',
  },
  unableToProcessRequest: {
    id: 'unableToProcessRequest',
    defaultMessage: 'Unable to process request',
  },
  unableToProcessRequestMessage: {
    id: 'unableToProcessRequestMessage',
    defaultMessage:
      "We're sorry. We are unable to process your request at this moment. Please try again. If this error persists, contact your library or OCLC support at <link>support@oclc.org</link> and include the text from this page in your message.",
  },
  invalidRequest: {
    id: 'invalidRequest',
    defaultMessage: 'Invalid request',
  },
  unableToProcessClientRequestMessage: {
    id: 'unableToProcessClientRequestMessage',
    defaultMessage:
      'We are unable to process your request. Please contact your library or OCLC support at <link>support@oclc.org</link> and include the text from this page in your message.',
  },
});

export const recoverPassword = defineMessages({
  defaultHeading: {
    id: 'RecoverPassword.defaultHeading',
    defaultMessage: 'Request new password',
  },
  defaultHeadingSuccess: {
    id: 'RecoverPassword.defaultHeadingSuccess',
    defaultMessage: 'New password requested',
  },
  defaultInstructionalText: {
    id: 'RecoverPassword.defaultInstructionalText',
    defaultMessage:
      'To reset your password, enter your username below. If we recognize your username, an email with instructions will be sent to your email address on file. Once your password is reset, your current password will no longer be accepted.',
  },
  defaultInstructionalTextSuccess: {
    id: 'RecoverPassword.defaultInstructionalTextSuccess',
    defaultMessage:
      'An email will be sent to the address on file with instructions for creating a new password. This link will expire within 24 hours of being sent.',
  },
  heading: {
    id: 'RecoverPassword.heading',
    defaultMessage: 'Request new {passwordLabels}',
  },
  headingSuccess: {
    id: 'RecoverPassword.headingSuccess',
    defaultMessage: 'New {passwordLabels} requested',
  },
  instructionalText: {
    id: 'RecoverPassword.instructionalText',
    defaultMessage:
      'To reset your {passwordLabels}, enter your {userNameLabels} below. If we recognize your {userNameLabels}, an email with instructions will be sent to your email address on file. Once your {passwordLabels} is reset, your current {passwordLabels} will no longer be accepted.',
  },
  instructionalTextSuccess: {
    id: 'RecoverPassword.instructionalTextSuccess',
    defaultMessage:
      'An email will be sent to the address on file with instructions for creating a new {passwordLabels}. This link will expire within 24 hours of being sent.',
  },
  request: {
    id: 'RecoverPassword.request',
    defaultMessage: 'Request',
  },
});

export const loginMessages = defineMessages({
  doNotHaveAccount: {
    id: 'Login.doNotHaveAccount',
    defaultMessage: "Don't have an account?",
  },
  defaultLoginHelpText: {
    id: 'Login.defaultLoginHelpText',
    defaultMessage: 'Signing in for the first time or forgot password?',
  },
  loginHelpText: {
    id: 'Login.loginHelpText',
    defaultMessage: 'Signing in for the first time or forgot {fieldName}?',
  },
});

export const createUser = defineMessages({
  givenName: {
    id: 'givenName',
    defaultMessage: 'Given name',
  },
  familyName: {
    id: 'familyName',
    defaultMessage: 'Surname',
  },
  emailAddress: {
    id: 'emailAddress',
    defaultMessage: 'Email',
  },
  givenNameIsRequired: {
    id: 'givenNameIsRequired',
    defaultMessage: 'Given name is required.',
  },
  familyNameIsRequired: {
    id: 'familyNameIsRequired',
    defaultMessage: 'Surname is required.',
  },
  emailIsRequired: {
    id: 'emailIsRequired',
    defaultMessage: 'Email is required.',
  },
  invalidEmailAddress: {
    id: 'invalidEmailAddress',
    defaultMessage: 'Enter a valid email.',
  },
});

export const fieldRequirements = defineMessages({
  passwordLengthRequirement: {
    id: 'passwordLengthRequirement',
    defaultMessage: 'Must be at least 9 characters',
  },
  usernameLengthRequirement: {
    id: 'usernameLengthRequirement',
    defaultMessage: 'Must be at least 3 characters',
  },
  usernameIsInvalid: {
    id: 'usernameIsInvalid',
    defaultMessage:
      'Do not use spaces, semicolons(;), colons(:), apostrophes(‘), commas(,), angle brackets(< >), brackets([ ]), or parentheses(( ))',
  },
  atLeastOneNumberOrSpecialChar: {
    id: 'atLeastOneNumberOrSpecialChar',
    defaultMessage:
      'Must contain at least one numeric or special character (e.g. 1, %, *, $ and so on)',
  },
  invalidPunctuationMarks: {
    id: 'invalidPunctuationMarks',
    defaultMessage:
      'Do not use semicolons(;), colons(:), apostrophes(‘) or periods(.)',
  },
});

export const serverErrors = defineMessages({
  INTERNAL_SERVER_ERROR: {
    id: 'INTERNAL_SERVER_ERROR',
    defaultMessage: 'Internal server error',
  },
  SERVICE_UNAVAILABLE: {
    id: 'SERVICE_UNAVAILABLE',
    defaultMessage: 'Service unavailable',
  },
  AUTH_RESPONSE_ERROR: {
    id: 'authResponseError',
    defaultMessage: 'Authentication response error',
  },
  DEPENDENT_SERVER_ERROR: {
    id: 'dependentServerError',
    defaultMessage: 'Dependent server error',
  },
  USER_RECORD_ERROR: {
    id: 'userRecordError',
    defaultMessage: 'User record error',
  },
  UNABLE_LOGIN_PROCESS: {
    id: 'unableLoginProcess',
    defaultMessage: 'Unable to complete login process',
  },
  UNABLE_LOGOUT_PROCESS: {
    id: 'unableLogoutProcess',
    defaultMessage: 'Unable to complete logout process',
  },
  BAD_REQUEST: {
    id: 'badRequest',
    defaultMessage: 'Bad request',
  },
  FORBIDDEN: {
    id: 'forbidden',
    defaultMessage: 'Forbidden',
  },
  NOT_FOUND: {
    id: 'notFound',
    defaultMessage: 'Not found',
  },
  METHOD_NOT_ALLOWED: {
    id: 'methodNotAllowed',
    defaultMessage: 'Method not allowed',
  },
});

export const errorDetails = defineMessages({
  STATUS_CODE: {
    id: 'ErrorDetails.statusCode',
    defaultMessage: 'Status code',
  },
  REASON: {
    id: 'ErrorDetails.reason',
    defaultMessage: 'Reason',
  },
  REQUEST_DATE: {
    id: 'ErrorDetails.requestDate',
    defaultMessage: 'Request date',
  },
  USER_AGENT: {
    id: 'ErrorDetails.userAgent',
    defaultMessage: 'User agent',
  },
  REQUEST_URI: {
    id: 'ErrorDetails.requestURI',
    defaultMessage: 'Request URI',
  },
  REFERRER: {
    id: 'ErrorDetails.referrer',
    defaultMessage: 'Referrer',
  },
  PARAMETERS: {
    id: 'ErrorDetails.parameters',
    defaultMessage: 'Parameters',
  },
});

const messages = defineMessages({
  ...common,
  ...createUser,
  ...errorMessages,
  ...authResult,
  ...recoverPassword,
  ...loginMessages,
  ...fieldRequirements,
  ...serverErrors,
  ...errorDetails,
});

export default messages;
